import React, { useEffect, useState } from 'react';

import { t } from 'i18next';

import { ReactComponent as SearchIcon } from '../../../../assets/icons/search.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/v3/close.svg';
import { IUserCompany } from '../../../interfaces/businessData';
import { TRANSLATION_COLLABORATORS } from '../../../translations/collaborators';
import { GenericVoidFunction } from '../../../types/genericTypes';

import './SearchBar.scss';

export interface ISearchBarV3 {
  onSearch: GenericVoidFunction;
  placeholder?: string;
  loading?: boolean;
  onChanged?: GenericVoidFunction;
  previousSearch?: string;
  company?: IUserCompany;
}

const SearchBarV3 = ({
  onSearch,
  placeholder = TRANSLATION_COLLABORATORS.table.searchBarPlaceholder,
  loading = false,
  onChanged,
  previousSearch,
  company
}: ISearchBarV3) => {
  const [searchValue, setSearchValue] = useState<string>(previousSearch ?? '');
  const [hovered, setHovered] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const [searched, setSearched] = useState<boolean>(false);

  useEffect(() => {
    if (previousSearch !== undefined) {
      setSearchValue(previousSearch);
    }
  }, [previousSearch]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);

    if (onChanged) {
      onChanged(newValue);
    }
  };

  const toggleSearch = () => {
    setSearched(searchValue != '');
    onSearch(searchValue);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    setSearched(false);
    if (event.key === 'Enter') {
      toggleSearch();
    }
  };

  const deleteSearch = () => {
    setSearchValue('');
    onSearch('');
    setSearched(false);
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setSearchValue('');
      if (onChanged) {
        onChanged('');
      }
    }
    return () => {
      isMounted = false;
    };
  }, [company]);

  return (
    <button
      className={`search-bar-v3 ${hovered ? 'hovered' : ''} ${focused ? 'focused' : ''} ${loading ? 'disabled' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
    >
      <SearchIcon
        className="search-icon"
        aria-label="icon-search"
        onClick={loading ? () => null : toggleSearch}
        data-testid="search-icon"
      />
      <input
        className="search-input"
        type="text"
        placeholder={t(placeholder)}
        onChange={handleSearch}
        onKeyPress={handleKeyPress}
        value={searchValue}
        disabled={loading}
        onFocus={() => {
          setHovered(false);
          setFocused(true);
        }}
        onBlur={() => setHovered(false)}
      />
      {searched && !loading && <CloseIcon className="close-icon" onClick={deleteSearch} data-testid="close-icon" />}
    </button>
  );
};

export default SearchBarV3;
